import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  UserPlus,
  Shield,
  Key,
  Building2,
  Mail,
  Phone,
  CreditCard,
  CheckCircle,
  XCircle,
  Lock,
  RefreshCw,
  Eye,
  EyeOff,
  MapPin,
  Banknote,
  QrCode,
  FileText,
  Upload,
  Download,
  File,
  ShieldCheck,
  ShieldOff,
  Calendar
} from "lucide-react";
import Setup2FA from "@/components/Setup2FA";

const API = process.env.REACT_APP_BACKEND_URL;

// Permissões pré-definidas por perfil
const PERMISSOES_POR_PERFIL = {
  MASTER_GERAL: [
    "acessar_clientes", "cadastrar_cliente", "editar_cliente", "excluir_cliente",
    "acessar_contratos", "cadastrar_contrato", "editar_contrato", "excluir_contrato",
    "acessar_comissoes", "editar_comissoes",
    "acessar_bancos", "cadastrar_banco", "editar_banco",
    "acessar_usuarios", "cadastrar_usuario", "editar_usuario", "excluir_usuario",
    "acessar_empresas", "cadastrar_empresa", "editar_empresa",
    "acessar_relatorios", "exportar_relatorios",
    "acessar_esteira_cliente", "mover_esteira",
    "acessar_financeiro", "aprovar_pagamentos",
    "acessar_configuracoes", "editar_configuracoes"
  ],
  ADMIN_EMPRESA: [
    "acessar_clientes", "cadastrar_cliente", "editar_cliente", "excluir_cliente",
    "acessar_contratos", "cadastrar_contrato", "editar_contrato",
    "acessar_comissoes", "editar_comissoes",
    "acessar_bancos", "cadastrar_banco", "editar_banco",
    "acessar_usuarios", "cadastrar_usuario", "editar_usuario",
    "acessar_relatorios", "exportar_relatorios",
    "acessar_esteira_cliente", "mover_esteira",
    "acessar_financeiro", "aprovar_pagamentos"
  ],
  CORRETOR: [
    "acessar_clientes", "cadastrar_cliente", "editar_cliente",
    "acessar_contratos", "cadastrar_contrato",
    "acessar_comissoes",
    "acessar_esteira_cliente", "mover_esteira"
  ],
  ATENDENTE: [
    "acessar_clientes", "cadastrar_cliente", "editar_cliente",
    "acessar_contratos", "cadastrar_contrato", "editar_contrato",
    "acessar_comissoes",
    "acessar_esteira_cliente", "mover_esteira",
    "acessar_bancos"
  ],
  TELEMARKETING: [
    "acessar_clientes", "cadastrar_cliente",
    "acessar_esteira_cliente", "mover_esteira"
  ],
  GESTOR_ADMINISTRATIVO: [
    "acessar_clientes", "cadastrar_cliente", "editar_cliente",
    "acessar_contratos", "cadastrar_contrato", "editar_contrato",
    "acessar_comissoes", "editar_comissoes",
    "acessar_relatorios", "exportar_relatorios",
    "acessar_financeiro",
    "acessar_esteira_cliente", "mover_esteira"
  ],
  GESTOR_OPERACIONAL: [
    "acessar_clientes", "cadastrar_cliente", "editar_cliente",
    "acessar_contratos", "cadastrar_contrato", "editar_contrato",
    "acessar_comissoes",
    "acessar_bancos",
    "acessar_esteira_cliente", "mover_esteira",
    "acessar_relatorios"
  ]
};

// Labels das permissões para exibição
const PERMISSOES_LABELS = {
  "acessar_clientes": "Acessar Clientes",
  "cadastrar_cliente": "Cadastrar Cliente",
  "editar_cliente": "Editar Cliente",
  "excluir_cliente": "Excluir Cliente",
  "acessar_contratos": "Acessar Contratos",
  "cadastrar_contrato": "Cadastrar Contrato",
  "editar_contrato": "Editar Contrato",
  "excluir_contrato": "Excluir Contrato",
  "acessar_comissoes": "Acessar Comissões",
  "editar_comissoes": "Editar Comissões",
  "acessar_bancos": "Acessar Bancos",
  "cadastrar_banco": "Cadastrar Banco",
  "editar_banco": "Editar Banco",
  "acessar_usuarios": "Acessar Usuários",
  "cadastrar_usuario": "Cadastrar Usuário",
  "editar_usuario": "Editar Usuário",
  "excluir_usuario": "Excluir Usuário",
  "acessar_empresas": "Acessar Empresas",
  "cadastrar_empresa": "Cadastrar Empresa",
  "editar_empresa": "Editar Empresa",
  "acessar_relatorios": "Acessar Relatórios",
  "exportar_relatorios": "Exportar Relatórios",
  "acessar_esteira_cliente": "Acessar Esteira de Clientes",
  "mover_esteira": "Mover na Esteira",
  "acessar_financeiro": "Acessar Financeiro",
  "aprovar_pagamentos": "Aprovar Pagamentos",
  "acessar_configuracoes": "Acessar Configurações",
  "editar_configuracoes": "Editar Configurações"
};

// Labels dos perfis
const PERFIL_LABELS = {
  MASTER_GERAL: "Master Geral",
  ADMIN_EMPRESA: "Admin da Empresa",
  CORRETOR: "Corretor",
  ATENDENTE: "Atendente",
  TELEMARKETING: "Telemarketing",
  GESTOR_ADMINISTRATIVO: "Gestor Administrativo",
  GESTOR_OPERACIONAL: "Gestor Operacional"
};

export default function GestaoUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [permissoesSistema, setPermissoesSistema] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroPerfil, setFiltroPerfil] = useState("todos");
  
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPermissoes, setModalPermissoes] = useState(false);
  const [modal2FA, setModal2FA] = useState(false);
  const [usuario2FA, setUsuario2FA] = useState(null);
  const [editando, setEditando] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Usuário logado
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [empresaLogada, setEmpresaLogada] = useState(null);
  const [permissoesLogado, setPermissoesLogado] = useState([]);
  
  // Form
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    cpf: "",
    telefone: "",
    senha: "",
    perfil: "ATENDENTE",
    empresa_id: "",
    status: "ATIVO",
    permissoes_extras: [],
    permissoes_removidas: [],
    // Endereço
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    // Dados Bancários
    banco_codigo: "",
    banco_nome: "",
    agencia: "",
    conta: "",
    tipo_conta: "CORRENTE",
    // PIX
    tipo_pix: "",
    chave_pix: "",
    // Código do usuário (gerado automaticamente)
    codigo_usuario: "",
    // Documentos
    documentos: []
  });
  
  // Estados para upload de documentos
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState("RG");

  // Tipos de documentos
  const TIPOS_DOCUMENTO = [
    "RG",
    "CPF",
    "CNH",
    "COMPROVANTE_RESIDENCIA",
    "CERTIDAO_NASCIMENTO",
    "TITULO_ELEITOR",
    "CTPS",
    "PIS",
    "RESERVISTA",
    "FOTO_3X4",
    "CONTRATO",
    "OUTRO"
  ];

  // Lista de UFs
  const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

  // Lista de bancos comuns
  const BANCOS = [
    { codigo: "001", nome: "Banco do Brasil" },
    { codigo: "033", nome: "Santander" },
    { codigo: "104", nome: "Caixa Econômica" },
    { codigo: "237", nome: "Bradesco" },
    { codigo: "341", nome: "Itaú" },
    { codigo: "041", nome: "Banrisul" },
    { codigo: "756", nome: "Sicoob" },
    { codigo: "748", nome: "Sicredi" },
    { codigo: "077", nome: "Inter" },
    { codigo: "260", nome: "Nubank" },
    { codigo: "336", nome: "C6 Bank" },
    { codigo: "212", nome: "Original" },
    { codigo: "422", nome: "Safra" },
    { codigo: "633", nome: "Rendimento" },
    { codigo: "655", nome: "Neon" },
    { codigo: "290", nome: "PagBank" }
  ];

  // Buscar CEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, "");
    if (cepLimpo.length !== 8) return;
    
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data && !response.data.erro) {
        setFormData(prev => ({
          ...prev,
          logradouro: response.data.logradouro || "",
          bairro: response.data.bairro || "",
          cidade: response.data.localidade || "",
          uf: response.data.uf || ""
        }));
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    }
  };

  // Gerar código do usuário baseado no UF
  const gerarCodigoUsuario = async (uf) => {
    if (!uf) return "";
    
    try {
      // Buscar último código para o UF
      const response = await axios.get(`${API}/api/usuarios-sistema?per_page=1000`);
      const usuarios = response.data?.usuarios || [];
      
      // Filtrar usuários com código do mesmo UF
      const usuariosUF = usuarios.filter(u => 
        u.codigo_usuario && u.codigo_usuario.startsWith(uf)
      );
      
      // Encontrar o maior número
      let maiorNumero = 0;
      usuariosUF.forEach(u => {
        const numero = parseInt(u.codigo_usuario.replace(uf, "")) || 0;
        if (numero > maiorNumero) maiorNumero = numero;
      });
      
      // Gerar próximo código
      const proximoNumero = maiorNumero + 1;
      return `${uf}${String(proximoNumero).padStart(4, "0")}`;
    } catch (error) {
      console.error("Erro ao gerar código:", error);
      return `${uf}0001`;
    }
  };

  useEffect(() => {
    // Carregar dados do localStorage
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    const empresa = JSON.parse(localStorage.getItem("empresa") || "{}");
    const permissoes = JSON.parse(localStorage.getItem("permissoes") || "[]");
    
    setUsuarioLogado(usuario);
    setEmpresaLogada(empresa);
    setPermissoesLogado(permissoes);
    
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const empresa = JSON.parse(localStorage.getItem("empresa") || "{}");
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      
      // Carregar usuários (filtrar por empresa se não for Master)
      let url = `${API}/api/usuarios-sistema?per_page=100`;
      if (usuario.perfil !== "MASTER_GERAL" && empresa.id) {
        url += `&empresa_id=${empresa.id}`;
      }
      const [usuariosRes, funcoesRes, permissoesRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/api/funcoes`),
        axios.get(`${API}/api/permissoes`)
      ]);
      
      setUsuarios(usuariosRes.data.usuarios || []);
      setFuncoes(funcoesRes.data.funcoes || []);
      setPermissoesSistema(permissoesRes.data.permissoes || []);
      
      // Se for Master, carregar empresas
      if (usuario.perfil === "MASTER_GERAL") {
        const empresasRes = await axios.get(`${API}/api/empresas?per_page=100`);
        setEmpresas(empresasRes.data.empresas || []);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Upload de documento
  const uploadDocumento = async (file) => {
    if (!editando?.id) {
      toast.error("Salve o usuário antes de anexar documentos");
      return;
    }
    
    setUploadingDoc(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("tipo_documento", tipoDocumento);
      
      const response = await axios.post(
        `${API}/api/usuarios-sistema/${editando.id}/documentos`,
        formDataUpload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      if (response.data.success) {
        // Atualizar lista de documentos local
        setFormData(prev => ({
          ...prev,
          documentos: [...(prev.documentos || []), response.data.documento]
        }));
        toast.success("Documento anexado com sucesso");
      }
    } catch (error) {
      console.error("Erro ao anexar documento:", error);
      toast.error(error.response?.data?.detail || "Erro ao anexar documento");
    } finally {
      setUploadingDoc(false);
    }
  };

  // Excluir documento
  const excluirDocumento = async (documentoId) => {
    if (!editando?.id) return;
    if (!confirm("Excluir este documento?")) return;
    
    try {
      await axios.delete(`${API}/api/usuarios-sistema/${editando.id}/documentos/${documentoId}`);
      
      // Atualizar lista local
      setFormData(prev => ({
        ...prev,
        documentos: (prev.documentos || []).filter(d => d.id !== documentoId)
      }));
      toast.success("Documento excluído");
    } catch (error) {
      toast.error("Erro ao excluir documento");
    }
  };

  const abrirModal = (usuario = null) => {
    if (usuario) {
      setEditando(usuario);
      setFormData({
        nome: usuario.nome || "",
        email: usuario.email || "",
        cpf: usuario.cpf || "",
        telefone: usuario.telefone || "",
        senha: "",
        perfil: usuario.perfil || "FUNCIONARIO",
        empresa_id: usuario.empresa_id || empresaLogada?.id || "",
        funcao_id: usuario.funcao_id || "",
        funcao_codigo: usuario.funcao_codigo || "",
        status: usuario.status || "ATIVO",
        permissoes_extras: usuario.permissoes_extras || [],
        permissoes_removidas: usuario.permissoes_removidas || [],
        // Endereço
        cep: usuario.cep || "",
        logradouro: usuario.logradouro || "",
        numero: usuario.numero || "",
        complemento: usuario.complemento || "",
        bairro: usuario.bairro || "",
        cidade: usuario.cidade || "",
        uf: usuario.uf || "",
        // Dados Bancários
        banco_codigo: usuario.banco_codigo || "",
        banco_nome: usuario.banco_nome || "",
        agencia: usuario.agencia || "",
        conta: usuario.conta || "",
        tipo_conta: usuario.tipo_conta || "CORRENTE",
        // PIX
        tipo_pix: usuario.tipo_pix || "",
        chave_pix: usuario.chave_pix || "",
        // Código
        codigo_usuario: usuario.codigo_usuario || ""
      });
    } else {
      setEditando(null);
      setFormData({
        nome: "",
        email: "",
        cpf: "",
        telefone: "",
        senha: "",
        perfil: "ATENDENTE",
        empresa_id: empresaLogada?.id || "",
        funcao_id: "",
        funcao_codigo: "",
        status: "ATIVO",
        permissoes_extras: [],
        permissoes_removidas: [],
        // Endereço
        cep: "",
        logradouro: "",
        numero: "",
        complemento: "",
        bairro: "",
        cidade: "",
        uf: "",
        // Dados Bancários
        banco_codigo: "",
        banco_nome: "",
        agencia: "",
        conta: "",
        tipo_conta: "CORRENTE",
        // PIX
        tipo_pix: "",
        chave_pix: "",
        // Código
        codigo_usuario: ""
      });
    }
    setModalOpen(true);
  };

  const salvarUsuario = async () => {
    if (!formData.nome || !formData.email) {
      toast.error("Nome e email são obrigatórios");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...formData };
      
      // Adicionar permissões do perfil automaticamente
      if (payload.perfil && PERMISSOES_POR_PERFIL[payload.perfil]) {
        payload.permissoes = PERMISSOES_POR_PERFIL[payload.perfil];
      }
      
      // Remover senha vazia se editando
      if (editando && !payload.senha) {
        delete payload.senha;
      }
      
      if (editando) {
        await axios.put(`${API}/api/usuarios-sistema/${editando.id}`, payload);
        toast.success("Usuário atualizado com sucesso");
      } else {
        if (!payload.senha) {
          toast.error("Senha é obrigatória para novos usuários");
          setSaving(false);
          return;
        }
        await axios.post(`${API}/api/usuarios-sistema`, payload);
        toast.success("Usuário criado com sucesso");
      }
      
      setModalOpen(false);
      carregarDados();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar usuário");
    } finally {
      setSaving(false);
    }
  };

  const excluirUsuario = async (usuario) => {
    if (!confirm(`Excluir usuário ${usuario.nome}?`)) return;
    
    try {
      await axios.delete(`${API}/api/usuarios-sistema/${usuario.id}`);
      toast.success("Usuário excluído");
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir");
    }
  };

  const alterarStatus = async (usuario, novoStatus) => {
    try {
      await axios.patch(`${API}/api/usuarios-sistema/${usuario.id}/status?status=${novoStatus}`);
      toast.success(`Status alterado para ${novoStatus}`);
      carregarDados();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  // Desabilitar 2FA de um usuário (apenas Master)
  const desabilitar2FA = async (usuario) => {
    const masterPassword = prompt("Digite a senha do Master para confirmar:");
    if (!masterPassword) return;
    
    try {
      const response = await axios.post(
        `${API}/api/auth/2fa/disable`,
        null,
        { params: { user_id: usuario.id, master_password: masterPassword } }
      );
      
      if (response.data.success) {
        toast.success(`2FA desabilitado para ${usuario.nome}`);
        carregarDados();
      } else {
        toast.error(response.data.message || "Erro ao desabilitar 2FA");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao desabilitar 2FA");
    }
  };

  const usuariosFiltrados = usuarios.filter(u => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!u.nome?.toLowerCase().includes(search) && 
          !u.email?.toLowerCase().includes(search) &&
          !u.codigo_usuario?.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (filtroStatus !== "todos" && u.status !== filtroStatus) return false;
    if (filtroPerfil !== "todos" && u.perfil !== filtroPerfil) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    const configs = {
      ATIVO: { variant: "default", className: "bg-green-500", icon: CheckCircle },
      INATIVO: { variant: "secondary", className: "bg-gray-500", icon: XCircle },
      BLOQUEADO: { variant: "destructive", className: "bg-red-500", icon: Lock },
      SUSPENSO: { variant: "outline", className: "bg-yellow-500 text-white", icon: XCircle }
    };
    const config = configs[status] || configs.INATIVO;
    const Icon = config.icon;
    return (
      <Badge className={`${config.className} gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getPerfilBadge = (perfil) => {
    const configs = {
      MASTER_GERAL: { className: "bg-purple-600 text-white", label: "Master Geral" },
      ADMIN_EMPRESA: { className: "bg-blue-600 text-white", label: "Admin" },
      CORRETOR: { className: "bg-green-600 text-white", label: "Corretor" },
      ATENDENTE: { className: "bg-orange-600 text-white", label: "Atendente" },
      TELEMARKETING: { className: "bg-cyan-600 text-white", label: "Telemarketing" },
      GESTOR_ADMINISTRATIVO: { className: "bg-indigo-600 text-white", label: "G. Admin" },
      GESTOR_OPERACIONAL: { className: "bg-rose-600 text-white", label: "G. Operac." }
    };
    const config = configs[perfil] || configs.ATENDENTE;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6" />
            Gestão de Usuários
          </h1>
          <p className="text-gray-500">Gerencie os usuários do sistema</p>
        </div>
        
        {permissoesLogado.includes("cadastrar_usuario") && (
          <Button onClick={() => abrirModal()} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Novo Usuário
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, email ou código..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos Status</SelectItem>
                <SelectItem value="ATIVO">Ativo</SelectItem>
                <SelectItem value="INATIVO">Inativo</SelectItem>
                <SelectItem value="BLOQUEADO">Bloqueado</SelectItem>
                <SelectItem value="SUSPENSO">Suspenso</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filtroPerfil} onValueChange={setFiltroPerfil}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Perfil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos Perfis</SelectItem>
                <SelectItem value="MASTER_GERAL">Master Geral</SelectItem>
                <SelectItem value="ADMIN_EMPRESA">Admin</SelectItem>
                <SelectItem value="CORRETOR">Corretor</SelectItem>
                <SelectItem value="ATENDENTE">Atendente</SelectItem>
                <SelectItem value="TELEMARKETING">Telemarketing</SelectItem>
                <SelectItem value="GESTOR_ADMINISTRATIVO">Gestor Administrativo</SelectItem>
                <SelectItem value="GESTOR_OPERACIONAL">Gestor Operacional</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" onClick={carregarDados}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>2FA</TableHead>
                <TableHead>Último Acesso</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usuariosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    Nenhum usuário encontrado
                  </TableCell>
                </TableRow>
              ) : (
                usuariosFiltrados.map((usuario) => {
                  // Buscar nome da empresa vinculada
                  const empresaVinculada = empresas.find(e => e.id === usuario.empresa_id);
                  const nomeEmpresa = empresaVinculada?.nome_fantasia || empresaVinculada?.razao_social || "---";
                  
                  return (
                  <TableRow key={usuario.id}>
                    <TableCell className="font-mono text-sm">
                      {usuario.codigo_usuario || "---"}
                    </TableCell>
                    <TableCell className="font-medium">{usuario.nome}</TableCell>
                    <TableCell>{usuario.email}</TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600">{nomeEmpresa}</span>
                    </TableCell>
                    <TableCell>{getPerfilBadge(usuario.perfil)}</TableCell>
                    <TableCell>{getStatusBadge(usuario.status)}</TableCell>
                    <TableCell>
                      {usuario.two_factor_enabled ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-gray-500">
                          <ShieldOff className="w-3 h-3 mr-1" />
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {usuario.ultimo_acesso 
                        ? new Date(usuario.ultimo_acesso).toLocaleString("pt-BR")
                        : "Nunca"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* Botão de configurar 2FA */}
                        {!usuario.two_factor_enabled && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-green-600"
                            onClick={() => {
                              setUsuario2FA(usuario);
                              setModal2FA(true);
                            }}
                            title="Configurar 2FA"
                            data-testid={`setup-2fa-${usuario.id}`}
                          >
                            <Shield className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Botão de desabilitar 2FA (apenas Master) */}
                        {usuario.two_factor_enabled && usuarioLogado?.perfil === "MASTER_GERAL" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-600"
                            onClick={() => desabilitar2FA(usuario)}
                            title="Desabilitar 2FA"
                          >
                            <ShieldOff className="h-4 w-4" />
                          </Button>
                        )}
                        {permissoesLogado.includes("editar_usuario") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => abrirModal(usuario)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {permissoesLogado.includes("excluir_usuario") && 
                         usuario.id !== usuarioLogado?.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => excluirUsuario(usuario)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal de Usuário */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editando ? <Edit className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
              {editando ? "Editar Usuário" : "Novo Usuário"}
            </DialogTitle>
            <DialogDescription>
              {editando ? "Atualize os dados do usuário" : "Preencha os dados do novo usuário"}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="dados" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="dados">Dados Pessoais</TabsTrigger>
              <TabsTrigger value="endereco">Endereço</TabsTrigger>
              <TabsTrigger value="bancario">Dados Bancários</TabsTrigger>
              <TabsTrigger value="documentos">Documentos</TabsTrigger>
              <TabsTrigger value="acesso">Acesso</TabsTrigger>
            </TabsList>

            <TabsContent value="dados" className="space-y-4 mt-4">
              <div className="grid gap-4 md:grid-cols-2">
                {/* Código do Usuário */}
                <div className="space-y-2">
                  <Label>Código do Usuário</Label>
                  <Input
                    value={formData.codigo_usuario}
                    readOnly
                    placeholder="Gerado automaticamente pelo UF"
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500">Gerado automaticamente após informar o UF</p>
                </div>
                <div className="space-y-2">
                  <Label>Nome Completo *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Nome do usuário"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CPF</Label>
                  <Input
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    placeholder="000.000.000-00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código do Usuário</Label>
                  <Input
                    value={formData.codigo_usuario}
                    onChange={(e) => setFormData({ ...formData, codigo_usuario: e.target.value.toUpperCase() })}
                    placeholder="Ex: PA0001"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500">Identificador único do usuário (ex: PA0001)</p>
                </div>
                <div className="space-y-2">
                  <Label>{editando ? "Nova Senha (deixe vazio para manter)" : "Senha *"}</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.senha}
                      onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                      placeholder={editando ? "••••••••" : "Senha inicial"}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 text-gray-400"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Data de Cadastro - só aparece ao editar */}
                {editando && editando.created_at && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      Data de Cadastro
                    </Label>
                    <div className="p-2 bg-slate-100 rounded-md text-sm text-gray-700 border">
                      {new Date(editando.created_at).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="endereco" className="space-y-4 mt-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>CEP</Label>
                  <Input
                    value={formData.cep}
                    onChange={(e) => {
                      const cep = e.target.value;
                      setFormData({ ...formData, cep });
                      if (cep.replace(/\D/g, "").length === 8) {
                        buscarCEP(cep);
                      }
                    }}
                    placeholder="00000-000"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Logradouro</Label>
                  <Input
                    value={formData.logradouro}
                    onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                    placeholder="Rua, Avenida, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="Nº"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Complemento</Label>
                  <Input
                    value={formData.complemento}
                    onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                    placeholder="Apto, Bloco, etc."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bairro</Label>
                  <Input
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    placeholder="Bairro"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    placeholder="Cidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UF *</Label>
                  <Select 
                    value={formData.uf} 
                    onValueChange={async (v) => {
                      setFormData({ ...formData, uf: v });
                      // Gerar código automaticamente se for novo usuário
                      if (!editando) {
                        const codigo = await gerarCodigoUsuario(v);
                        setFormData(prev => ({ ...prev, uf: v, codigo_usuario: codigo }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent>
                      {UFS.map((uf) => (
                        <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="bancario" className="space-y-4 mt-4">
              <div className="space-y-4">
                {/* Conta Bancária */}
                <div className="p-4 border rounded-lg bg-slate-50">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Conta Bancária
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Banco</Label>
                      <Select 
                        value={formData.banco_codigo} 
                        onValueChange={(v) => {
                          const banco = BANCOS.find(b => b.codigo === v);
                          setFormData({ 
                            ...formData, 
                            banco_codigo: v,
                            banco_nome: banco?.nome || ""
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o banco" />
                        </SelectTrigger>
                        <SelectContent>
                          {BANCOS.map((banco) => (
                            <SelectItem key={banco.codigo} value={banco.codigo}>
                              {banco.codigo} - {banco.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo de Conta</Label>
                      <Select 
                        value={formData.tipo_conta} 
                        onValueChange={(v) => setFormData({ ...formData, tipo_conta: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CORRENTE">Conta Corrente</SelectItem>
                          <SelectItem value="POUPANCA">Conta Poupança</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Agência</Label>
                      <Input
                        value={formData.agencia}
                        onChange={(e) => setFormData({ ...formData, agencia: e.target.value })}
                        placeholder="0000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Conta</Label>
                      <Input
                        value={formData.conta}
                        onChange={(e) => setFormData({ ...formData, conta: e.target.value })}
                        placeholder="00000-0"
                      />
                    </div>
                  </div>
                </div>

                {/* PIX */}
                <div className="p-4 border rounded-lg bg-green-50">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <QrCode className="h-4 w-4 text-green-600" />
                    Chave PIX
                  </h4>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Tipo de Chave</Label>
                      <Select 
                        value={formData.tipo_pix} 
                        onValueChange={(v) => setFormData({ ...formData, tipo_pix: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CPF">CPF</SelectItem>
                          <SelectItem value="CNPJ">CNPJ</SelectItem>
                          <SelectItem value="EMAIL">E-mail</SelectItem>
                          <SelectItem value="TELEFONE">Telefone</SelectItem>
                          <SelectItem value="ALEATORIA">Chave Aleatória</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Chave PIX</Label>
                      <Input
                        value={formData.chave_pix}
                        onChange={(e) => setFormData({ ...formData, chave_pix: e.target.value })}
                        placeholder="Informe a chave PIX"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="documentos" className="space-y-4 mt-4">
              <div className="space-y-4">
                {/* Upload de documento */}
                <div className="p-4 border-2 border-dashed rounded-lg bg-slate-50">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Upload className="h-4 w-4 text-blue-600" />
                    Anexar Documento
                  </h4>
                  {!editando?.id ? (
                    <p className="text-sm text-orange-600">
                      ⚠️ Salve o usuário primeiro para poder anexar documentos
                    </p>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label>Tipo de Documento</Label>
                        <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                          <SelectTrigger>
                            <SelectValue placeholder="Tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            {TIPOS_DOCUMENTO.map((tipo) => (
                              <SelectItem key={tipo} value={tipo}>
                                {tipo.replace(/_/g, " ")}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Arquivo</Label>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadDocumento(file);
                          }}
                          disabled={uploadingDoc}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Lista de documentos */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Documentos Anexados ({formData.documentos?.length || 0})
                  </div>
                  {(!formData.documentos || formData.documentos.length === 0) ? (
                    <div className="p-6 text-center text-slate-400">
                      Nenhum documento anexado
                    </div>
                  ) : (
                    <div className="divide-y">
                      {formData.documentos.map((doc) => (
                        <div 
                          key={doc.id}
                          className="px-3 py-2 flex items-center justify-between hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-3">
                            <File className="h-5 w-5 text-blue-500" />
                            <div>
                              <p className="font-medium text-sm">{doc.nome_original}</p>
                              <p className="text-xs text-slate-500">
                                {doc.tipo?.replace(/_/g, " ")} • {Math.round((doc.tamanho || 0) / 1024)} KB
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(doc.url, "_blank")}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => excluirDocumento(doc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="acesso" className="space-y-4 mt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Perfil *</Label>
                  <Select 
                    value={formData.perfil} 
                    onValueChange={(v) => setFormData({ ...formData, perfil: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o perfil" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuarioLogado?.perfil === "MASTER_GERAL" && (
                        <>
                          <SelectItem value="MASTER_GERAL">Master Geral</SelectItem>
                          <SelectItem value="ADMIN_EMPRESA">Admin da Empresa</SelectItem>
                        </>
                      )}
                      <SelectItem value="CORRETOR">Corretor - Parceiro Externo</SelectItem>
                      <SelectItem value="ATENDENTE">Atendente - Funcionário</SelectItem>
                      <SelectItem value="TELEMARKETING">Telemarketing - Funcionário</SelectItem>
                      <SelectItem value="GESTOR_ADMINISTRATIVO">Gestor Administrativo - Funcionário</SelectItem>
                      <SelectItem value="GESTOR_OPERACIONAL">Gestor Operacional - Funcionário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {usuarioLogado?.perfil === "MASTER_GERAL" && formData.perfil !== "MASTER_GERAL" && (
                  <div className="space-y-2">
                    <Label>Empresa *</Label>
                    <Select 
                      value={formData.empresa_id} 
                      onValueChange={(v) => setFormData({ ...formData, empresa_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a empresa" />
                      </SelectTrigger>
                      <SelectContent>
                        {empresas.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.nome_fantasia || emp.razao_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ATIVO">Ativo</SelectItem>
                      <SelectItem value="INATIVO">Inativo</SelectItem>
                      <SelectItem value="BLOQUEADO">Bloqueado</SelectItem>
                      <SelectItem value="SUSPENSO">Suspenso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Permissões do Perfil */}
              <div className="space-y-3 mt-4">
                <Label className="flex items-center gap-2 text-base font-semibold">
                  <Shield className="h-5 w-5 text-blue-600" />
                  Permissões do Perfil: {PERFIL_LABELS[formData.perfil] || formData.perfil}
                </Label>
                <div className="p-4 bg-slate-50 rounded-lg border">
                  {formData.perfil && PERMISSOES_POR_PERFIL[formData.perfil] ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {PERMISSOES_POR_PERFIL[formData.perfil].map((perm) => (
                        <div key={perm} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>{PERMISSOES_LABELS[perm] || perm}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Selecione um perfil para ver as permissões</p>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  As permissões são pré-definidas para cada perfil e aplicadas automaticamente ao salvar.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarUsuario} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Configuração 2FA */}
      <Setup2FA
        usuario={usuario2FA}
        isOpen={modal2FA}
        onClose={() => {
          setModal2FA(false);
          setUsuario2FA(null);
        }}
        onSuccess={() => {
          carregarDados();
          toast.success("2FA configurado com sucesso!");
        }}
      />
    </div>
  );
}
